import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';

import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL } from '@/constants/Config';
import type { StoreShippingOptionType } from '@/modules/Orders/CreateOrders/StoreCheckout/constants/storeCheckout.constants';

export interface IParticipantHqProfile {
  fullName: string;
  email: string;
  phone: string | null;
}

export interface IParticipantHqSession {
  authenticated: boolean;
  email?: string;
  profile?: IParticipantHqProfile | null;
}

export interface IParticipantHqOrderCard {
  participantId: number;
  storeName: string;
  status: string;
  statusKey: string;
  /** True when the store closed without meeting its minimum. */
  isFailedOrder?: boolean;
  productImage: string;
  orderedByLabel: string;
  storeTipped: boolean;
  /** True when any design group this participant ordered from has hit its MOQ. */
  minimumHit: boolean;
  showOrderNumber: boolean;
  orderNumber?: number;
  createdAt?: string;
}

export interface IParticipantHqAddress {
  shippingName: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface IParticipantHqOrderDetail {
  participantId: number;
  storeName: string;
  storeTipped: boolean;
  status: string;
  statusKey: string;
  /** True when the store closed without meeting its minimum. */
  isFailedOrder?: boolean;
  orderNumber?: number;
  /** The participant's own shipping address from their store checkout row. */
  address: IParticipantHqAddress;
  /** Bulk stores ship to one destination held on the order; null when individual. */
  orderAddress?: IParticipantHqAddress | null;
  shippingType?: StoreShippingOptionType;
  /** Parent store order id; the `groupOrderId` the storefront share link points at. */
  storeId?: number;
  /** True while the store still accepts checkouts, so the participant can share it. */
  storeLive?: boolean;
  /** Close date for timed stores; null for permanent stores. */
  groupOrderDate?: string | null;
  isPermanentStore?: boolean;
  orderItems: Array<{
    id: number;
    orderItemId: string;
    status: number;
    sizes: string;
    quantity: number;
    numUnits: number;
    price: number;
    productTitle?: string;
    primaryImage?: string;
    secondaryImage?: string;
    trackingNumber?: string;
    regOrderTrackLink?: string;
    apparel: {
      name: string;
      brand: string;
      styleCode: string;
      color: string;
      hexCode: string;
    };
    design: {
      id?: number;
      primaryImage?: string;
      secondaryImage?: string;
      apparelStyleCode?: string;
      apparelColor?: string;
      printTypeInfo?: unknown[];
      decorMethodInfo?: unknown;
    };
  }>;
  fromDeliveryDate?: string | null;
  toDeliveryDate?: string | null;
  dueDate?: string | null;
  estimatedDelivery?: string | null;
  guaranteeDelivery?: boolean;
  summary: {
    subtotal: number;
    shipping: number;
    salesTax: number;
    discount: number;
    orderTotal: number;
  };
}

const rawBaseQuery = axiosBaseQuery({
  baseUrl: API_SERVER_URL,
});

const unwrapSuccessData = (body: unknown): unknown => {
  if (!body || typeof body !== 'object') {
    return body;
  }
  const record = body as Record<string, unknown>;
  if (record.status === 'success' && 'data' in record) {
    return record.data;
  }
  return body;
};

const participantHqBaseQuery: BaseQueryFn<
  {
    url: string;
    method: string | undefined;
    data?: unknown;
    params?: unknown;
  },
  unknown,
  unknown
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result && typeof result === 'object' && 'error' in result) {
    return result;
  }

  return { data: unwrapSuccessData(result) };
};

export const participantHqQuery = createApi({
  reducerPath: 'participantHqQuery',
  tagTypes: ['ParticipantHqSession', 'ParticipantHqOrders'],
  baseQuery: participantHqBaseQuery,
  endpoints: (build) => ({
    getParticipantSession: build.query<IParticipantHqSession, void>({
      query: () => ({
        url: 'api/participant-hq/session',
        method: 'get',
      }),
      providesTags: ['ParticipantHqSession'],
    }),
    getParticipantOrders: build.query<{ orders: IParticipantHqOrderCard[] }, void>({
      query: () => ({
        url: 'api/participant-hq/orders',
        method: 'get',
      }),
      providesTags: ['ParticipantHqOrders'],
    }),
    getParticipantOrder: build.query<{ order: IParticipantHqOrderDetail }, string | number>({
      query: (participantId) => ({
        url: `api/participant-hq/orders/${participantId}`,
        method: 'get',
      }),
    }),
    participantLogout: build.mutation<{ status: string }, void>({
      query: () => ({
        url: 'api/participant-hq/session/logout',
        method: 'post',
      }),
      invalidatesTags: ['ParticipantHqSession', 'ParticipantHqOrders'],
    }),
  }),
});

export const {
  useGetParticipantSessionQuery,
  useGetParticipantOrdersQuery,
  useGetParticipantOrderQuery,
  useParticipantLogoutMutation,
} = participantHqQuery;
